import { yupResolver } from '@hookform/resolvers/yup';
import Button from '@shared/components/Button/Button';
import InputGroup from '@shared/components/InputGroup/InputGroup';
import Popup from '@shared/components/Popup/Popup';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ArrowRight } from 'assets/icons/ArrowRight.svg';
import { ReactComponent as BuildingCommunity } from 'assets/icons/BuildingCommunity.svg';
import { ReactComponent as Loader } from 'assets/icons/Loader.svg';
import GradientIconWithShadow from 'components/GradientIconWithShadow/GradientIconWithShadow';
import ModalFooter from 'components/ModalFooter';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useTheme from 'contexts/ThemeProvider/useTheme';
import useNoPermissionToast from 'hooks/useNoPermissionToast';
import { calculatePermissions } from 'hooks/useWorkspacePermissions/utils';
import { AliasStatusEnum } from 'modules/CreateNewRoomModal/CreateNewRoomDataProvider/utils';
import { slugifyExtended } from 'modules/CreateNewRoomModal/CreateRoomStepModal/AliasField/utils';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import { isWorkspaceAliasEnabled } from 'modules/Workspaces/WorkspacesProvider/utils';
import { Schemas } from 'utils/yup/schemas/validationSchemas';

import useCreateWorkspace from './useCreateWorkspace';
import useWorkspaceAliasAvailability from './useWorkspaceAliasAvailability';
import WorkspaceFormField from './WorkspaceFormField';
import ParentWorkspaceSelector from './WorkspaceTypeSelector/ParentWorkspaceSelector';
import useCreateWorkspaceDataContext from '../CreateWorkspaceDataProvider/useCreateWorkspaceDataContext';
import { CreateWorkspaceStepsEnum } from '../types';

export type WorkspaceCreateFormType = {
  workspaceName: string;
  workspaceAlias: string;
  parentWorkspaceId: string | null;
};

interface WorkspaceCreateStepProps {
  onGoBack?: () => void;
}

const WorkspaceCreateStep = ({ onGoBack }: WorkspaceCreateStepProps) => {
  const { t } = useTranslation();
  const { matrixClient } = useMatrixContext();
  const pushNoPermissionToast = useNoPermissionToast();
  const {
    removeModal,
    setCurrentStep,
    parentWorkspaceId: initialParentWorkspaceId,
    setCreatedWorkspaceId,
  } = useCreateWorkspaceDataContext();
  const { workspacesState } = useWorkspacesContext();
  const [hasAliasChanged, setHasAliasChanged] = useState(false);
  const [frozenParentVisible, setFrozenParentVisible] = useState<boolean | null>(null);
  const {
    formState: { errors, isValid },
    handleSubmit: onSubmitForm,
    control,
    watch,
    setValue,
    trigger,
  } = useForm<WorkspaceCreateFormType>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: yupResolver(Schemas.getWorkspaceCreateSchema()),
    defaultValues: {
      workspaceName: '',
      workspaceAlias: '',
      parentWorkspaceId: initialParentWorkspaceId,
    },
  });
  const { createWorkspace, isCreatingWorkspace, isWaitingForBot, pendingBotRetryRoomId, retryBotJoin } =
    useCreateWorkspace();
  const { aliasStatus, isAliasCheckLoading } = useWorkspaceAliasAvailability(
    watch('workspaceAlias'),
    !!errors.workspaceAlias
  );

  const isAliasUnavailable = aliasStatus === AliasStatusEnum.UNAVAILABLE;
  const isAliasAvailable = !isWorkspaceAliasEnabled || aliasStatus === AliasStatusEnum.AVAILABLE;
  const parentWorkspaceId = watch('parentWorkspaceId');

  const advanceToInviteStep = (roomId: string) => {
    setCreatedWorkspaceId(roomId);
  };

  const handleSubmit = async ({ workspaceName, workspaceAlias, parentWorkspaceId }: WorkspaceCreateFormType) => {
    if (!calculatePermissions(matrixClient, parentWorkspaceId).createSubWorkspace) return pushNoPermissionToast();

    setFrozenParentVisible(!!parentWorkspaces.length);

    const response = await createWorkspace({
      name: workspaceName.trim(),
      alias: workspaceAlias.trim(),
      parentWorkspaceId,
      onCreated: advanceToInviteStep,
    });

    if (response) setCurrentStep(CreateWorkspaceStepsEnum.INVITE);
  };

  // AC-284: retry re-runs the bot-join gate on the same workspace room (no
  // second `createRoom`). On success, advance to the invite step.
  const handleBotJoinRetry = async () => {
    const ok = await retryBotJoin(advanceToInviteStep);
    if (ok) setCurrentStep(CreateWorkspaceStepsEnum.INVITE);
  };

  const [handleCancel, cancelText] = onGoBack ? [onGoBack, t('back')] : [removeModal, t('cancel')];
  const { appDomain } = useTheme();

  const workspaceUrlPlaceholder = appDomain + '/';
  const parentWorkspaces = workspacesState.rootWorkspaces.filter(
    ({ isSubWorkspace, workspaceId }) =>
      !isSubWorkspace && calculatePermissions(matrixClient, workspaceId).createSubWorkspace
  );

  const isParentWorkspaceSelectionVisible =
    frozenParentVisible !== null ? frozenParentVisible : !!parentWorkspaces.length;

  return (
    <Popup isVisible closePopup={handleCancel} wrapperId="create-workspace-modal">
      <Popup.FullScreenContainer className="md:w-[616px]">
        <div className="flex flex-col grow overflow-y-auto p-4">
          <div className="flex flex-col items-center justify-center w-full py-6 text-center">
            <GradientIconWithShadow icon={BuildingCommunity} />
            <h1 className="text-2xl font-medium mb-2 mt-8 text-size-lg">{t('workspaces.create')}</h1>
            <p className="max-w-100">{t('workspaces.create_subtitle')}</p>
          </div>
          {isWaitingForBot && (
            <p className="text-size-sm text-gray-80 text-center" data-testid="bot-join-waiting">
              {t('workspaces.creating_workspace_waiting_for_bot')}
            </p>
          )}
          {pendingBotRetryRoomId && !isWaitingForBot && (
            <div className="flex flex-col items-center gap-2 py-2">
              <p className="text-size-sm text-gray-80 text-center">{t('workspaces.bot_join_timeout')}</p>
              <Button variant="secondaryTertiary" size="sm" onClick={handleBotJoinRetry} data-testid="bot-join-retry">
                {t('try_again')}
              </Button>
            </div>
          )}
          <form onSubmit={onSubmitForm(handleSubmit)} className="space-y-4">
            {isParentWorkspaceSelectionVisible && (
              <WorkspaceFormField label={t('workspaces.select')}>
                <ParentWorkspaceSelector
                  selectedParentWorkspace={parentWorkspaceId}
                  onParentWorkspaceChange={(value) => setValue('parentWorkspaceId', value, { shouldValidate: true })}
                />
              </WorkspaceFormField>
            )}
            <WorkspaceFormField label={t('workspaces.name')}>
              <InputGroup errorMessage={errors.workspaceName?.message}>
                <Controller
                  name="workspaceName"
                  control={control}
                  render={({ field: { onChange, value, onBlur } }) => (
                    <InputGroup.Input
                      value={value || ''}
                      onChange={(e) => {
                        const newValue = e.target.value;
                        onChange(newValue);

                        if (!hasAliasChanged && isWorkspaceAliasEnabled) {
                          const alias = slugifyExtended(newValue);
                          setValue('workspaceAlias', alias, { shouldDirty: true });
                        }
                        trigger('workspaceName');
                      }}
                      onBlur={() => {
                        onBlur();
                        trigger('workspaceAlias');
                      }}
                      type="text"
                      placeholder={t('workspaces.name')}
                      hasError={!!errors.workspaceName?.message}
                      autoComplete="off"
                    />
                  )}
                />
              </InputGroup>
            </WorkspaceFormField>
            <WorkspaceFormField label={t('workspaces.url')} isVisible={isWorkspaceAliasEnabled}>
              <InputGroup
                errorMessage={
                  errors.workspaceAlias?.message || (isAliasUnavailable ? t('workspaces.alias_unavailable') : undefined)
                }
              >
                <InputGroup.TextBlock>{workspaceUrlPlaceholder}</InputGroup.TextBlock>
                <Controller
                  name="workspaceAlias"
                  control={control}
                  render={({ field: { onChange, value, onBlur } }) => (
                    <InputGroup.InputBlock
                      value={value || ''}
                      onChange={(e) => {
                        const newValue = e.target.value;

                        onChange(newValue);
                        setHasAliasChanged(true); // Mark alias as manually changed when user edits this field to no longer sync with workspace name field
                        trigger('workspaceAlias');
                      }}
                      onBlur={onBlur}
                      type="text"
                      placeholder={t('workspaces.url')}
                      hasError={!!errors.workspaceAlias?.message || isAliasUnavailable}
                      autoComplete="off"
                      icon={isAliasCheckLoading ? Loader : undefined}
                      iconClassName="animate-spin"
                      iconPosition="end"
                    />
                  )}
                />
              </InputGroup>
            </WorkspaceFormField>
          </form>
        </div>
        <ModalFooter
          onCloseClick={handleCancel}
          onSubmit={onSubmitForm(handleSubmit)}
          successButtonText={t('next')}
          successButtonIcon={ArrowRight}
          successButtonIconPosition="after"
          cancelText={cancelText}
          successDisabled={!isValid || !isAliasAvailable}
          isLoading={isCreatingWorkspace}
        />
      </Popup.FullScreenContainer>
    </Popup>
  );
};

export default WorkspaceCreateStep;
